import React from "react";
import { Check, X } from "lucide-react";
import type {
  AdminPortalPermissionMatrix,
  GroupedPermissionsView,
  Resource,
} from "../../../features/rbac/types";

interface AdminPortalMatrixProps {
  matrix: AdminPortalPermissionMatrix | null;
  resources: Resource[];
}

const cell = (value?: boolean) =>
  value ? (
    <Check className="mx-auto h-4 w-4 text-emerald-600" />
  ) : (
    <X className="mx-auto h-4 w-4 text-gray-300" />
  );

export const AdminPortalPermissionMatrixView: React.FC<AdminPortalMatrixProps> = ({
  matrix,
  resources,
}) => {
  if (!matrix) return null;

  const entries = Object.entries(matrix);
  if (entries.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        No admin portal permissions resolved for this role-resource.
      </p>
    );
  }

  const resourceName = (resourceId: string) =>
    resources.find((r) => String(r.resourceId) === resourceId)?.resourceName ?? `Resource ${resourceId}`;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-cardborder bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
            <th className="px-4 py-2">Resource</th>
            <th className="px-4 py-2 text-center">Read</th>
            <th className="px-4 py-2 text-center">Create</th>
            <th className="px-4 py-2 text-center">Update</th>
            <th className="px-4 py-2 text-center">Delete</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([resourceId, value]) => (
            <tr key={resourceId} className="border-b border-gray-100 last:border-0">
              <td className="px-4 py-2 font-medium text-gray-800">
                {resourceName(resourceId)}{" "}
                <span className="text-xs text-gray-400">#{resourceId}</span>
              </td>
              <td className="px-4 py-2 text-center">{cell(value.read)}</td>
              <td className="px-4 py-2 text-center">{cell(value.create)}</td>
              <td className="px-4 py-2 text-center">{cell(value.update)}</td>
              <td className="px-4 py-2 text-center">{cell(value.delete)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface GroupedPermissionsViewProps {
  grouped: GroupedPermissionsView | null;
}

export const GroupedPermissionsListView: React.FC<GroupedPermissionsViewProps> = ({ grouped }) => {
  if (!grouped) return null;
  const entries = Object.entries(grouped);
  if (entries.length === 0) {
    return <p className="text-sm text-gray-500">No grouped permissions for this role-resource.</p>;
  }
  return (
    <div className="space-y-3">
      {entries.map(([resourceName, actions]) => (
        <div key={resourceName} className="rounded-md border border-cardborder">
          <div className="border-b border-cardborder bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-600">
            {resourceName}
          </div>
          <div className="grid grid-cols-1 gap-2 px-3 py-3 md:grid-cols-2">
            {Object.entries(actions).map(([action, entriesForAction]) => (
              <div key={action} className="text-sm">
                <div className="font-medium text-gray-800">{action}</div>
                <div className="text-xs text-gray-500">
                  {entriesForAction.length} permission(s):{" "}
                  {entriesForAction.map((e) => `#${e.permissionId}`).join(", ")}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
